import { Component } from '@theme/component';
import { getScrollContainer, scrollContainerMediaQuery } from '@theme/scroll-container';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A floating button that scrolls the page back to the top.
 *
 * Appears once the shopper has scrolled roughly one viewport height down the
 * page scroll container and hides again near the top. The scroll container is
 * `.page-wrapper` in the squeeze layout (desktop ≥990px) and the document root
 * on mobile, so the scroll listener is rebound whenever that boundary flips.
 *
 * @example
 * <scroll-to-top-component class="scroll-to-top" data-visible="false">
 *   <button type="button" on:click="/scrollToTop" aria-label="...">...</button>
 * </scroll-to-top-component>
 *
 * @extends {Component}
 */
class ScrollToTopComponent extends Component {
  /** @type {AbortController} Aborts listeners tied to the component lifetime. */
  #abortController = new AbortController();

  /** @type {AbortController | null} Aborts the scroll listener, recreated when the scroll container changes. */
  #scrollAbortController = null;

  /** @type {boolean} Whether a visibility update is already scheduled for the next frame. */
  #frameScheduled = false;

  /** @type {boolean} */
  #visible = false;

  connectedCallback() {
    super.connectedCallback();

    // Recreate the controller each connect: disconnectedCallback aborts it, and an
    // aborted signal makes addEventListener a no-op, so a reconnect would otherwise
    // silently drop the media-query listener.
    this.#abortController = new AbortController();

    scrollContainerMediaQuery.addEventListener('change', this.#bindScrollListener, {
      signal: this.#abortController.signal,
    });
    window.addEventListener('resize', this.#handleScroll, { signal: this.#abortController.signal });

    this.#bindScrollListener();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#scrollAbortController?.abort();
    this.#abortController.abort();
  }

  /**
   * Re-applies the visibility state after a Section Rendering API re-render,
   * since morph may reset the host's `data-visible` attribute to the
   * server-rendered default.
   */
  updatedCallback() {
    super.updatedCallback();
    this.#applyVisibility();
  }

  /**
   * Scrolls the page scroll container back to the top.
   * Bound declaratively via `on:click="/scrollToTop"`.
   */
  scrollToTop() {
    const container = this.#getScrollContainer();
    const buyBox = this.#getBuyBox();
    const containerTop = container.matches('.page-wrapper') ? container.getBoundingClientRect().top : 0;
    const headerHeight = document.querySelector('#header-component')?.getBoundingClientRect().height ?? 0;
    const purchaseOptions = buyBox?.querySelector('bundle-selector-component, .mgsp__tiers, product-form-component, form[action*="/cart/add"]') ?? buyBox;
    const addButton = buyBox?.querySelector('button[name="add"]');
    const viewportHeight = container.matches('.page-wrapper') ? container.clientHeight : window.innerHeight;
    const optionsTop = purchaseOptions ? container.scrollTop + purchaseOptions.getBoundingClientRect().top - containerTop - headerHeight - 16 : 0;
    const addButtonTop = addButton ? container.scrollTop + addButton.getBoundingClientRect().bottom - containerTop - viewportHeight + 24 : 0;
    const top = Math.max(0, optionsTop, addButtonTop);
    container.scrollTo({
      top,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * (Re)binds the scroll listener to the current scroll event target.
   * Runs on connect and whenever the 990px squeeze-layout media query flips,
   * because the scroll container switches between `.page-wrapper` and the
   * document root.
   */
  #bindScrollListener = () => {
    this.#scrollAbortController?.abort();
    this.#scrollAbortController = new AbortController();

    document.addEventListener('scroll', this.#handleScroll, {
      passive: true,
      signal: this.#scrollAbortController.signal,
    });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', this.#handleScroll, {
      passive: true,
      signal: this.#scrollAbortController.signal,
    });

    this.#updateVisibility();
  };

  /**
   * Throttles visibility updates to one per animation frame.
   */
  #handleScroll = () => {
    if (this.#frameScheduled) return;
    this.#frameScheduled = true;

    requestAnimationFrame(() => {
      this.#frameScheduled = false;
      this.#updateVisibility();
    });
  };

  /**
   * Shows the button after ~1 viewport height of scrolling, hides it near the top.
   */
  #updateVisibility() {
    const container = this.#getScrollContainer();
    this.#visible = container.scrollTop > container.clientHeight;
    if (this.classList.contains('scroll-to-top--above-sticky-cta')) {
      const addButton = this.#getBuyBox()?.querySelector('button[name="add"]');
      const addRect = addButton?.getBoundingClientRect();
      const headerHeight = document.querySelector('#header-component')?.getBoundingClientRect().height ?? 0;
      if (addRect && addRect.top >= headerHeight && addRect.bottom <= innerHeight) this.#visible = false;
      let bottom = 16;
      const button = this.querySelector('button')?.getBoundingClientRect();
      for (const bar of document.querySelectorAll('.mgsp__sticky, .sticky-add-to-cart__bar, .mg-adv__sticky-cta')) {
        if (!bar.checkVisibility()) continue;
        const rect = bar.getBoundingClientRect();
        if (button && rect.top < innerHeight && rect.bottom > 0 && rect.right > button.left && rect.left < button.right) {
          bottom = Math.max(bottom, innerHeight - rect.top + 12);
        }
      }
      this.style.setProperty('--scroll-to-top-bottom', `${bottom}px`);
    }
    this.#applyVisibility();
  }

  #getScrollContainer() {
    const container = getScrollContainer();
    if (container.matches('.page-wrapper') && !['auto', 'scroll'].includes(getComputedStyle(container).overflowY)) {
      return document.scrollingElement ?? document.documentElement;
    }
    return container;
  }

  #getBuyBox() {
    return this.classList.contains('scroll-to-top--above-sticky-cta')
      ? document.querySelector('[data-mgsp-buy-box], #MainContent [data-testid="product-information-details"], .mg-adv__offer')
      : null;
  }

  /**
   * Syncs the host's `data-visible` attribute with the tracked state.
   */
  #applyVisibility() {
    const value = this.#visible ? 'true' : 'false';
    if (this.dataset.visible !== value) this.dataset.visible = value;
  }
}

if (!customElements.get('scroll-to-top-component')) {
  customElements.define('scroll-to-top-component', ScrollToTopComponent);
}
